import React, { useState } from 'react';
import { useParams, useNavigate, useOutletContext, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import { palette } from '../../theme/colors';
import { tokenManager } from '../../services/authService';
import { b2bService, PortalInfo, B2bAccessRequest } from '../../services/b2bService';

interface PortalContext {
  portalInfo: PortalInfo;
  isAuthenticated: boolean;
  user: ReturnType<typeof tokenManager.getUser>;
  rejectedRequest?: B2bAccessRequest;
}

interface AccessRequestForm {
  companyName: string;
  phone: string;
  message: string;
}

export const B2BAccessRequestPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const context = useOutletContext<PortalContext>();
  const { portalInfo, isAuthenticated, user, rejectedRequest } = context || {};

  const [formData, setFormData] = useState<AccessRequestForm>({
    companyName: rejectedRequest?.companyName || '',
    phone: user?.phone || '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: AccessRequestForm) => b2bService.requestAccess(orgSlug!, data),
    onSuccess: () => {
      setSubmitted(true);
      // Invalidate access status query to reflect new request
      queryClient.invalidateQueries({ queryKey: ['b2b-access-status', orgSlug] });
    },
  });

  const handleChange = (field: keyof AccessRequestForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: 8 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: palette.primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <PersonIcon sx={{ color: palette.primary, fontSize: 48 }} />
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Account Required
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          You need to be logged in to request B2B access. Please log in or create an account first.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            component={RouterLink}
            to={`/login?redirect=${encodeURIComponent(`/b2b/${orgSlug}/request-access`)}`}
            variant="contained"
            size="large"
          >
            Login
          </Button>
          <Button
            component={RouterLink}
            to={`/register?redirect=${encodeURIComponent(`/b2b/${orgSlug}/request-access`)}`}
            variant="outlined"
            size="large"
          >
            Create Account
          </Button>
        </Box>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: 8 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: palette.successLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <CheckCircleIcon sx={{ color: palette.success, fontSize: 48 }} />
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Request Submitted!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Thank you for your interest in partnering with {portalInfo?.organization.name}.
          Our team will review your application and get back to you within 24-48 hours.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          You will receive a notification at <strong>{user?.email}</strong> once your request is reviewed.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/b2b/${orgSlug}`)}
          >
            Back to Portal
          </Button>
          <Button
            component={RouterLink}
            to="/account/b2b-access"
            variant="contained"
          >
            View My Requests
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/b2b/${orgSlug}`)}
        sx={{ mb: 3, color: palette.textSecondary }}
      >
        Back to Portal
      </Button>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                bgcolor: palette.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <BusinessIcon sx={{ color: '#fff', fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {rejectedRequest ? 'Request Access Again' : 'Request B2B Access'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {rejectedRequest
                ? `Submit a new request to access ${portalInfo?.organization.name}'s B2B portal.`
                : `Fill out the form below to request access to ${portalInfo?.organization.name}'s B2B portal.`}
            </Typography>
          </Box>

          {/* Show previous rejection reason if re-requesting */}
          {rejectedRequest?.reviewNotes && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Previous rejection reason:</strong> {rejectedRequest.reviewNotes}
              </Typography>
            </Alert>
          )}

          {mutation.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {mutation.error instanceof Error ? mutation.error.message : 'Failed to submit request'}
            </Alert>
          )}

          {/* Show current user info as read-only */}
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Requesting as:
            </Typography>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Company Name"
              value={formData.companyName}
              onChange={handleChange('companyName')}
              required
              fullWidth
              sx={{ mb: 2 }}
              helperText="Your business or company name"
            />

            <TextField
              label="Phone Number"
              value={formData.phone}
              onChange={handleChange('phone')}
              fullWidth
              sx={{ mb: 2 }}
              helperText="Optional contact number"
            />

            <TextField
              label="Message"
              value={formData.message}
              onChange={handleChange('message')}
              multiline
              rows={4}
              fullWidth
              placeholder="Tell us about your business and why you'd like to partner with us..."
              sx={{ mb: 3 }}
              helperText="Optional message to the organization"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={mutation.isPending}
              sx={{ py: 1.5 }}
            >
              {mutation.isPending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
